import React, { useEffect } from 'react'
import {
  ComboBox,
  Dropdown,
  IComboBoxOption,
  IDropdownOption,
  Label,
  MessageBarType,
  Separator,
  TextField,
} from 'office-ui-fabric-react'
import { useQuery } from '@apollo/react-hooks'
import { client } from '../../../Api/api'
import InfoBox from '../../CampaignCollectionScreen/CampaignDialog/InfoBox'
import {
  getAllOutstreamPlacementIntegrationsQuery,
  OutstreamPlacementInfo,
  OutstreamPlacementIntegrationType,
} from '../../../Api/outstreamPlacements_v2'
import { AdFrameOptionsField } from './FixedPositionForm'

// Marks a pick that is not an R2B2 replacement placement of this domain, so saving is
// blocked. Never persisted: the dialog runs getErrorCode() before every mutation.
export const UNLISTED_R2B2_STICKY = 'r2b2StickyPlacementUnlisted'

export function InReadToR2B2Form({
  integration,
  integrationTypeConfig,
  onIntegrationTypeConfigChange,
  disabled,
  errorCode,
  isAdmin,
}: {
  integration?: string
  integrationTypeConfig: any
  onIntegrationTypeConfigChange: (config: any) => void
  disabled: boolean
  errorCode: string
  isAdmin: boolean
}) {
  const onTextChange =
    (field: string) =>
    (
      _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ): void => {
      onIntegrationTypeConfigChange({
        ...integrationTypeConfig,
        [field]: newValue || '',
      })
    }
  const onDropdownChange =
    (field: string) =>
    (
      _event: React.FormEvent<HTMLDivElement>,
      item: IDropdownOption | undefined
    ): void => {
      if (!item) return
      onIntegrationTypeConfigChange({
        ...integrationTypeConfig,
        [field]: item.key as string,
      })
    }
  const onR2B2StickyChange = (field: string) => (placementId: string) => {
    onIntegrationTypeConfigChange({
      ...integrationTypeConfig,
      [field]: placementId,
    })
  }
  const {
    options: r2b2StickyOptions,
    loading,
    error,
  } = useR2B2ReplacementOptions(integration)
  const hasUnlistedSticky =
    !loading &&
    !error &&
    [
      integrationTypeConfig.r2b2StickyPlacementMobile,
      integrationTypeConfig.r2b2StickyPlacementDesktop,
    ].some(
      (placementId) =>
        !!placementId &&
        !r2b2StickyOptions.some((option) => option.key === placementId)
    )

  useEffect(() => {
    if (!!integrationTypeConfig[UNLISTED_R2B2_STICKY] === hasUnlistedSticky)
      return
    const config = { ...integrationTypeConfig }
    if (hasUnlistedSticky) {
      config[UNLISTED_R2B2_STICKY] = true
    } else {
      delete config[UNLISTED_R2B2_STICKY]
    }
    onIntegrationTypeConfigChange(config)
  })
  return (
    <>
      <TextField
        label="Insert after selector"
        description="The carousel goes after the first matching element"
        placeholder="article .content > p:nth-of-type(2)"
        errorMessage={errorCode === 'invalidSelector' ? 'Invalid selector' : ''}
        value={integrationTypeConfig.insertAfterSelector || ''}
        autoComplete="off"
        disabled={disabled}
        onChange={onTextChange('insertAfterSelector')}
      />
      <Separator />
      <Label>Floating</Label>
      <div
        style={{
          display: 'grid',
          gap: '16px 28px',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <Dropdown
          label="Horizontal Container Position"
          disabled={disabled}
          selectedKey={
            integrationTypeConfig.horizontalContainerPosition || 'right'
          }
          options={[
            { key: 'left', text: 'Left' },
            { key: 'right', text: 'Right' },
          ]}
          onChange={onDropdownChange('horizontalContainerPosition')}
        />
        <Dropdown
          label="Vertical Container Position"
          disabled={disabled}
          selectedKey={
            integrationTypeConfig.verticalContainerPosition || 'bottom'
          }
          options={[
            { key: 'top', text: 'Top' },
            { key: 'bottom', text: 'Bottom' },
          ]}
          onChange={onDropdownChange('verticalContainerPosition')}
        />
      </div>
      <Separator />
      <Label>
        R2B2 sticky
        {integration ? `, R2B2 replacement placements of ${integration}` : ''}
      </Label>
      <R2B2StickyComboBox
        label="Mobile placement"
        options={r2b2StickyOptions}
        value={integrationTypeConfig.r2b2StickyPlacementMobile}
        disabled={disabled}
        loading={loading}
        error={error}
        onChange={onR2B2StickyChange('r2b2StickyPlacementMobile')}
      />
      <R2B2StickyComboBox
        label="Desktop & tablet placement"
        options={r2b2StickyOptions}
        value={integrationTypeConfig.r2b2StickyPlacementDesktop}
        disabled={disabled}
        loading={loading}
        error={error}
        onChange={onR2B2StickyChange('r2b2StickyPlacementDesktop')}
      />
      <InfoBox type={MessageBarType.info}>
        The picked placement is an ordinary placement and also delivers on its
        own. Whitelist it to a glob that never matches, so that only the
        carousel hands over to it.
      </InfoBox>
      <Separator />
      <TextField
        label="Custom CSS"
        placeholder=":root { --caroda-floating-container-bottom-offset: 80px; }"
        value={
          integrationTypeConfig.playerOptions
            ? integrationTypeConfig.playerOptions.css || ''
            : ''
        }
        autoComplete="off"
        disabled={disabled}
        multiline={true}
        rows={5}
        onChange={(
          _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
          newValue?: string
        ): void => {
          onIntegrationTypeConfigChange({
            ...integrationTypeConfig,
            playerOptions: {
              ...(integrationTypeConfig.playerOptions || {}),
              css: newValue || '',
            },
          })
        }}
      />
      {isAdmin ? (
        <AdFrameOptionsField
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={onIntegrationTypeConfigChange}
          disabled={disabled}
        />
      ) : null}
    </>
  )
}

// The player loads the script of the picked R2B2 replacement placement
function useR2B2ReplacementOptions(integration?: string) {
  const { data, loading, error } = useQuery(
    getAllOutstreamPlacementIntegrationsQuery,
    {
      fetchPolicy: 'network-only',
      client,
    }
  )
  return {
    options: getR2B2ReplacementOptions(
      (data && data.outstreamPlacementCollections_v2) || [],
      integration
    ),
    loading,
    error,
  }
}

export function getR2B2ReplacementOptions(
  collections: { assets: OutstreamPlacementInfo[] }[],
  integration?: string
): IComboBoxOption[] {
  const options: IComboBoxOption[] = []
  for (const { assets } of collections) {
    for (const placement of assets) {
      const { id, title, integrationType } = placement
      if (
        integrationType ===
          OutstreamPlacementIntegrationType.R2B2_REPLACEMENT &&
        placement.integration === integration
      ) {
        options.push({
          key: id,
          text: title,
        })
      }
    }
  }
  return options.sort((a, b) => a.text.localeCompare(b.text))
}

function R2B2StickyComboBox({
  label,
  options,
  value,
  disabled,
  loading,
  error,
  onChange,
}: {
  label: string
  options: IComboBoxOption[]
  value?: string
  disabled: boolean
  loading: boolean
  error?: Error
  onChange: (placementId: string) => void
}) {
  // Keep a deleted or other-domain placement visible, instead of silently unselected
  const isListed = !value || options.some((option) => option.key === value)
  return (
    <ComboBox
      label={label}
      placeholder="None, the carousel keeps floating"
      autoComplete="on"
      allowFreeform={false}
      useComboBoxAsMenuWidth={true}
      disabled={disabled || loading}
      selectedKey={value || null}
      options={[
        { key: '', text: 'None' },
        ...options,
        ...(isListed
          ? []
          : [
              {
                key: value!,
                text: loading ? 'Loading…' : 'Not on this domain',
              },
            ]),
      ]}
      errorMessage={
        error
          ? 'Could not load R2B2 replacement placements'
          : !loading && value && !isListed
            ? 'Not an R2B2 replacement placement of this domain'
            : ''
      }
      onChange={(_event, option) => {
        if (option) {
          onChange(option.key as string)
        }
      }}
    />
  )
}
